// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { produce } from 'immer';
import { isDefined } from '@shared/utils';
import type {
  AccountCurrency,
  ClientHoldings,
  GetAdvisoryAccountsPositionsResponseBody,
} from '@shared/data-access/types/brokerage-types';

/**
 * Converts a numeric value using the provided exchange rate if the value is not
 * null (divides by rate: value in source currency / rate = value in target
 * currency).
 *
 * @param value - The numeric value to convert (can be null or undefined).
 * @param rate - The exchange rate to apply (divide by).
 * @returns The converted value or null if the original value was null/undefined.
 */
const convertValue = (
  value: number | null | undefined,
  rate: number,
): number | null => {
  return isDefined(value) ? value / rate : null;
};

/**
 * Gets the exchange rate from a holding for the target currency (usd_rate or
 * eur_rate). Defaults to 1 if the rate is missing or invalid.
 */
const getHoldingRate = (
  holding: ClientHoldings,
  targetCurrency: AccountCurrency,
): number => {
  const rate = targetCurrency === 'USD' ? holding.usd_rate : holding.eur_rate;
  return rate !== null && Number.isFinite(rate) ? rate : 1;
};

/**
 * Converts currency values for a holding using the holding's usd_rate or eur_rate
 * (divide by rate to get value in target currency).
 *
 * @param holding - The holding to convert (must have usd_rate and eur_rate).
 * @param targetCurrency - Target currency ('USD' or 'EUR').
 */
const convertHolding = (
  holding: ClientHoldings,
  targetCurrency: AccountCurrency,
): void => {
  const rate = getHoldingRate(holding, targetCurrency);
  holding.price /= rate;
  holding.investment /= rate;
  holding.acquisition_cost = convertValue(holding.acquisition_cost, rate);
};

/**
 * Converts the currency of accounts positions' holdings using each holding's
 * usd_rate or eur_rate (divide by rate to get value in target currency).
 *
 * @param accountsPositions - An array of account positions with holdings that
 *   include usd_rate and eur_rate.
 * @param targetCurrency - Target currency ('USD' or 'EUR').
 * @returns An array of account positions with converted currency values using immer
 *   for immutable updates.
 */
export const convertAccountsPositionsCurrency = (
  accountsPositions: GetAdvisoryAccountsPositionsResponseBody,
  targetCurrency: AccountCurrency,
): GetAdvisoryAccountsPositionsResponseBody => {
  if (accountsPositions.length === 0) {
    return [];
  }

  return produce(accountsPositions, draft => {
    for (const account of draft) {
      for (const holding of account.holdings) {
        convertHolding(holding, targetCurrency);
      }
    }
  });
};
